/**
 * Proyecto: MOGOTAX-SERVER
 * Autor: Megatrack GPS SAS
 * Fecha de creación: 8 de noviembre 2025
 * Licencia: Propiedad Privada
 *
 * Este código es propiedad exclusiva de Megatrack GPS SAS & Mogotax SAS y no puede ser distribuido, copiado, modificado, publicado, sublicenciado ni comercializado sin el consentimiento expreso y por escrito de ambas empresa.
 *
 * Copyright (c) 2025 Megatrack GPS SAS. Todos los derechos reservados.
 */
import React, { useState } from 'react';
import { Modal, Button, Checkbox, Typography, Divider, Space, message } from 'antd';
import { CheckCircleOutlined, FileTextOutlined } from '@ant-design/icons';
import { useTerms } from '../Contexts/TermsContext';

const { Title, Paragraph, Text } = Typography;

const TermsModal = () => {
  const { mostrarModalTerminos, aceptarTerminos, TYC_VERSION } = useTerms();
  const [aceptado, setAceptado] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleAceptar = async () => {
    if (!aceptado) {
      message.warning('Debes aceptar los términos y condiciones para continuar');
      return;
    }

    setLoading(true);
    const success = await aceptarTerminos();
    setLoading(false);

    if (success) {
      message.success('Términos y condiciones aceptados correctamente');
    } else {
      message.error('Error al aceptar los términos. Por favor intenta nuevamente');
    }
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FileTextOutlined style={{ fontSize: 24, color: '#1890ff' }} />
          <span>Términos y Condiciones</span>
        </div>
      }
      open={mostrarModalTerminos}
      closable={false}
      maskClosable={false}
      width={window.innerWidth > 768 ? 700 : '95%'}
      footer={[
        <div key="footer" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Checkbox
            checked={aceptado}
            onChange={(e) => setAceptado(e.target.checked)}
            style={{ fontSize: '14px' }}
          >
            He leído y acepto los términos y condiciones de uso de la aplicación MOGOTAX
          </Checkbox>
          <Button
            type="primary"
            size="large"
            icon={<CheckCircleOutlined />}
            onClick={handleAceptar}
            disabled={!aceptado}
            loading={loading}
            block
          >
            Aceptar y Continuar
          </Button>
        </div>
      ]}
    >
      <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '0 8px' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Text type="secondary">Versión: {TYC_VERSION} | Fecha: Noviembre 2025</Text>
          </div>

          <Divider />

          <div>
            <Title level={5}>1. ACEPTACIÓN DE LOS TÉRMINOS</Title>
            <Paragraph>
              Al acceder y utilizar la aplicación MOGOTAX para conductores, usted acepta
              estar sujeto a estos Términos y Condiciones de uso. Si no está de acuerdo
              con alguno de estos términos, no debe utilizar esta aplicación.
            </Paragraph>
          </div>

          <div>
            <Title level={5}>2. DESCRIPCIÓN DEL SERVICIO</Title>
            <Paragraph>
              MOGOTAX es una plataforma de gestión de traslados de pacientes que permite
              a los conductores registrados:
            </Paragraph>
            <ul>
              <li>Recibir asignaciones de traslados de pacientes</li>
              <li>Actualizar el estado de los traslados en tiempo real</li>
              <li>Registrar información de inicio y finalización de servicios</li>
              <li>Capturar firmas digitales de pacientes o acompañantes</li>
              <li>Mantener comunicación con el centro de operaciones</li>
            </ul>
          </div>

          <div>
            <Title level={5}>3. OBLIGACIONES DEL CONDUCTOR</Title>
            <Paragraph>Como conductor de MOGOTAX, usted se compromete a:</Paragraph>
            <ul>
              <li>Mantener actualizada su información personal y de contacto</li>
              <li>Realizar los traslados asignados de manera profesional y segura</li>
              <li>Actualizar el estado de cada traslado en tiempo real</li>
              <li>Registrar correctamente la firma del paciente o acompañante al finalizar cada servicio</li>
              <li>Tratar a los pacientes con respeto, dignidad y profesionalismo</li>
              <li>Cumplir con todas las normativas de tránsito y transporte sanitario</li>
              <li>Mantener el vehículo en condiciones óptimas de funcionamiento y limpieza</li>
              <li>Reportar cualquier incidente o emergencia de manera inmediata</li>
            </ul>
          </div>

          <div>
            <Title level={5}>4. RESPONSABILIDADES</Title>
            <Paragraph>
              El conductor es responsable de:
            </Paragraph>
            <ul>
              <li>La integridad y seguridad del paciente durante el traslado</li>
              <li>El uso adecuado del vehículo y equipos asignados</li>
              <li>La exactitud de la información registrada en la aplicación</li>
              <li>El cumplimiento de los horarios y rutas establecidas</li>
              <li>Mantener la confidencialidad de la información de los pacientes</li>
            </ul>
          </div>

          <div>
            <Title level={5}>5. USO DE LA APLICACIÓN</Title>
            <Paragraph>
              El conductor se compromete a utilizar la aplicación únicamente para:
            </Paragraph>
            <ul>
              <li>Gestionar los traslados asignados por MOGOTAX</li>
              <li>Actualizar el estado de los servicios en tiempo real</li>
              <li>Comunicarse con el centro de operaciones</li>
              <li>Registrar información relacionada con los traslados</li>
            </ul>
            <Paragraph>
              Está prohibido utilizar la aplicación para fines diferentes a los establecidos,
              compartir las credenciales de acceso, o intentar acceder a información no autorizada.
            </Paragraph>
          </div>

          <div>
            <Title level={5}>6. PROTECCIÓN DE DATOS</Title>
            <Paragraph>
              MOGOTAX se compromete a proteger la privacidad y confidencialidad de la información
              de pacientes y conductores. El conductor acepta que sus datos personales serán
              tratados conforme a nuestra Política de Privacidad y la normativa vigente de
              protección de datos personales.
            </Paragraph>
          </div>

          <div>
            <Title level={5}>7. FIRMA DIGITAL</Title>
            <Paragraph>
              El conductor debe registrar su firma digital en la aplicación. Esta firma se
              utilizará para:
            </Paragraph>
            <ul>
              <li>Validar la identidad del conductor en los documentos de control de servicio</li>
              <li>Certificar la realización de los traslados</li>
              <li>Generar documentación legal relacionada con los servicios prestados</li>
            </ul>
            <Paragraph>
              La firma digital tiene el mismo valor legal que una firma manuscrita y el
              conductor es responsable de su uso adecuado.
            </Paragraph>
          </div>

          <div>
            <Title level={5}>8. GEOLOCALIZACIÓN</Title>
            <Paragraph>
              La aplicación utiliza servicios de geolocalización para:
            </Paragraph>
            <ul>
              <li>Rastrear la ubicación del conductor durante los traslados</li>
              <li>Optimizar rutas y tiempos de servicio</li>
              <li>Garantizar la seguridad del paciente y del conductor</li>
              <li>Generar registros para auditoría y control de calidad</li>
            </ul>
            <Paragraph>
              Al aceptar estos términos, el conductor autoriza expresamente el uso de
              geolocalización durante el uso de la aplicación.
            </Paragraph>
          </div>

          <div>
            <Title level={5}>9. CAUSALES DE SUSPENSIÓN O TERMINACIÓN</Title>
            <Paragraph>
              MOGOTAX se reserva el derecho de suspender o terminar el acceso del conductor
              a la aplicación en caso de:
            </Paragraph>
            <ul>
              <li>Incumplimiento de estos términos y condiciones</li>
              <li>Conducta inapropiada hacia pacientes o personal</li>
              <li>Falsificación de información o documentos</li>
              <li>No actualización del estado de los traslados</li>
              <li>Negligencia en el cumplimiento de las responsabilidades</li>
              <li>Uso indebido de la aplicación o de la información</li>
            </ul>
          </div>

          <div>
            <Title level={5}>10. MODIFICACIONES</Title>
            <Paragraph>
              MOGOTAX se reserva el derecho de modificar estos términos y condiciones en
              cualquier momento. Los cambios serán notificados a través de la aplicación
              y el conductor deberá aceptar las nuevas versiones para continuar utilizando
              el servicio.
            </Paragraph>
          </div>

          <div>
            <Title level={5}>11. CONTACTO</Title>
            <Paragraph>
              Para cualquier consulta relacionada con estos términos y condiciones, puede
              contactarnos a través de:
            </Paragraph>
            <ul>
              <li>Email: soporte@mogotaxsas.com</li>
              <li>Teléfono: +57 (601) XXX-XXXX</li>
            </ul>
          </div>

          <Divider />

          <div style={{ textAlign: 'center', paddingBottom: 16 }}>
            <Text type="secondary" italic>
              Al hacer clic en "Aceptar y Continuar", confirmo que he leído, entendido
              y acepto cumplir con todos los términos y condiciones establecidos anteriormente.
            </Text>
          </div>
        </Space>
      </div>
    </Modal>
  );
};

export default TermsModal;
